import { ChangeEvent, useEffect } from 'react';
import { memo, useRef } from 'react';
import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { WebFormNumericFieldProps } from '../FormNumericField.types';
import { NumericPatternMask } from '../NumericPatternMask/NumericPatternMask';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const WebFormNumericField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
    }: WebFormNumericFieldProps) => {
        /**
         * Not all formik validation accounts for TextFields that are null.
         * So even though we pass an undefined validation type to the function,
         * it is still required in order to have null required fields validate.
         */
        const [field, meta] = useField<number | null>({
            name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: undefined,
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * Intended to ensure the masking is handled correctly by ensuring a re-render.
         * May not be needed.
         */
        const valueRef = useRef<number | null>(field.value ?? null);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            if (field.value !== valueRef.current) {
                valueRef.current = field.value;
            }
        }, [field.value]);

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            /**
             * Grabs the value from the event object.
             */
            const value: string = event.target.value;
            /**
             * Converts the string to a number since we know it will only be numeric values in the string.
             * If the string is null or empty, we set the number to 0.
             */
            const valueToNumber: number | null = value === '' ? null : +value;

            valueRef.current = valueToNumber;
            setFieldValue(name, valueToNumber);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'numeric'}
                type={'text'}
                inputRef={inputRef}
                value={valueRef.current !== null ? valueRef.current.toString() : ''}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onChange={handleOnChange}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                InputProps={{
                    inputComponent: NumericPatternMask as any,
                }}
            />
        );
    },
);
